import { z } from 'zod'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { basicEnrichment, interestConflictEnrichment } from './constants'
import { formSchema } from './schema'
import { UseFormReturn } from 'react-hook-form'
import { useState, useEffect, useCallback } from 'react'
import { RadioButton } from '@/components/ui/radiobutton'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import Tooltip from '@/components/compliance-assistant/Tooltip'
import TooltipInformation from '@/assets/TooltipInformation'
import Warning from '@/assets/Warning'
import ProcessInfoPanel, { depthLevelKey } from './ProcessInfoPanel'

interface ProcessFormFieldsProps {
  form: UseFormReturn<z.infer<typeof formSchema>>
  enrichmentTypes: EnrichmentType[] | undefined
  resetProcessData: () => void
  isViewMode: boolean
}

export default function ProcessFormFields({
  form,
  enrichmentTypes,
  resetProcessData,
  isViewMode,
}: ProcessFormFieldsProps) {
  const [selectedEnrichmentType, setSelectedEnrichmentType] = useState<EnrichmentType | null>(null)
  const [infoPanelKey, setInfoPanelKey] = useState<string | null>(null)
  const isBasic = selectedEnrichmentType?.naturalKey === basicEnrichment
  const isConflict = selectedEnrichmentType?.naturalKey === interestConflictEnrichment

  const handleOpenInfoPanel = useCallback((naturalKey: string) => {
    setInfoPanelKey(naturalKey)
  }, [])

  const handleCloseInfoPanel = useCallback(() => {
    setInfoPanelKey(null)
  }, [])

  useEffect(() => {
    // Check if form enrichment_type is set (is editing)
    const enrichmentType = form.getValues().enrichment_type
    if (enrichmentType) {
      // Set selected enrichment type
      const selected = enrichmentTypes?.find((item) => item.id === enrichmentType) || null
      setSelectedEnrichmentType(selected)
    }
  }, [form.getValues()])

  useEffect(() => {
    if (isBasic) {
      form.setValue('level_a', 1)
      form.setValue('level_b', 1)
    }
  }, [isBasic])

  useEffect(() => {
    if (selectedEnrichmentType) {
      // If there's only 1 type, select it by default.
      if (selectedEnrichmentType.downloadableTypes.length === 1) {
        form.setValue('downloadable_types', [selectedEnrichmentType.downloadableTypes[0].id], {
          shouldValidate: true,
        })
      }
    }
  }, [selectedEnrichmentType, form])

  return (
    <>
      {/* Nombre cliente */}
      <FormField
        control={form.control}
        name="client_name"
        render={({ field, fieldState }) => {
          const showIndicator = fieldState.invalid && !field.value
          return (
            <FormItem className="space-y-0">
              <FormLabel className="form-label">Nombre cliente</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    placeholder="Ingresar nombre"
                    className="placeholder-[#BDBDBD]"
                    disabled={isViewMode}
                    {...field}
                  />
                </div>
              </FormControl>
              {showIndicator ? (
                <div className="flex items-center mt-1 gap-1 text-red-600">
                  <Warning className="w-3 h-3" />
                  <span className="text-[0.8rem]">Campo requerido</span>
                </div>
              ) : (
                <FormMessage className="text-red-600" />
              )}
            </FormItem>
          )
        }}
      />

      {/* Nombre procesamiento */}
      <FormField
        control={form.control}
        name="name"
        render={({ field, fieldState }) => {
          const showIndicator = fieldState.invalid && !field.value
          return (
            <FormItem className="space-y-0">
              <FormLabel className="form-label">Nombre procesamiento</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    placeholder="Ingresar nombre"
                    className="placeholder-[#BDBDBD]"
                    disabled={isViewMode}
                    {...field}
                  />
                </div>
              </FormControl>
              {showIndicator ? (
                <div className="flex items-center mt-1 gap-1 text-red-600">
                  <Warning className="w-3 h-3" />
                  <span className="text-[0.8rem]">Campo requerido</span>
                </div>
              ) : (
                <FormMessage className="text-red-600" />
              )}
            </FormItem>
          )
        }}
      />

      <div className="form-title-label pt-5">Configuración procesamiento</div>
      <div className="flex flex-col space-y-4">
        <div className="flex flex-row">
          <FormField
            control={form.control}
            name="enrichment_type"
            render={({ field, fieldState }) => {
              const showIndicator = fieldState.invalid && !field.value
              const showValidationError = !showIndicator && fieldState.error

              return (
                <FormItem>
                  <FormLabel className="form-label">Tipo proceso</FormLabel>
                  <div className="relative flex flex-col w-full">
                    <RadioGroupPrimitive.Root
                      className="flex flex-row space-x-4"
                      onValueChange={(value) => {
                        const numericValue = Number(value)
                        field.onChange(numericValue)
                        const selected =
                          enrichmentTypes?.find((item) => item.id === numericValue) || null
                        setSelectedEnrichmentType(selected)
                        resetProcessData()
                      }}
                      value={
                        field.value !== undefined && field.value !== null ? String(field.value) : ''
                      }
                    >
                      {enrichmentTypes?.map((item: EnrichmentType) => (
                        <FormItem
                          key={item.id}
                          className="flex flex-row items-center space-y-0 pb-3"
                        >
                          <FormControl>
                            <RadioButton
                              className="form-radio"
                              value={String(item.id)}
                              disabled={isViewMode}
                            />
                          </FormControl>
                          <span className="flex items-center gap-1.5 ml-2 mr-4">
                            <FormLabel className="font-normal">{item.name}</FormLabel>
                            <Tooltip align="start" content="Ver detalle del proceso">
                              <button
                                type="button"
                                className="inline-flex items-center justify-center"
                                onClick={() => handleOpenInfoPanel(item.naturalKey)}
                                aria-label={`Más información sobre ${item.name}`}
                              >
                                <TooltipInformation />
                              </button>
                            </Tooltip>
                          </span>
                        </FormItem>
                      ))}
                    </RadioGroupPrimitive.Root>
                  </div>
                  {showIndicator && (
                    <div className="flex items-center mt-1 gap-1 text-red-600">
                      <Warning className="w-3 h-3" />
                      <span className="text-[0.8rem]">Selección requerida</span>
                    </div>
                  )}
                  {showValidationError && <FormMessage />}
                </FormItem>
              )
            }}
          />
        </div>

        <div className="flex flex-row w-full">
          <FormField
            control={form.control}
            name="downloadable_types"
            render={({ field, fieldState }) => {
              const formValues = form.getValues()
              const showIndicator =
                (fieldState.invalid &&
                  (!field.value || field.value.length === 0) &&
                  formValues.enrichment_type) ||
                fieldState.error
              return (
                <FormItem>
                  <FormLabel className="form-label">Tipo de descargable</FormLabel>
                  <div className="flex flex-row">
                    {selectedEnrichmentType?.downloadableTypes.map((item) => (
                      <FormItem
                        key={item.id}
                        className="relative flex flex-row items-start space-y-0 pb-3"
                      >
                        <FormControl>
                          <Checkbox
                            className="form-checkbox"
                            checked={field.value?.includes(item.id) ?? false}
                            onCheckedChange={(checked) => {
                              const current = field.value || []
                              if (checked) {
                                field.onChange([...current, item.id])
                              } else {
                                field.onChange(current.filter((id: number) => id !== item.id))
                              }
                            }}
                            disabled={isViewMode}
                          />
                        </FormControl>
                        <FormLabel className="font-normal ml-2 mr-4">{item.name}</FormLabel>
                      </FormItem>
                    ))}
                  </div>
                  {showIndicator && (
                    <div className="flex items-center mt-1 gap-1 text-red-600">
                      <Warning className="w-3 h-3" />
                      <span className="text-[0.8rem]">Selecciona al menos un tipo de descargable</span>
                    </div>
                  )}
                </FormItem>
              )
            }}
          />
        </div>
      </div>

      <div className="form-title-label pt-5">Configuración avanzada</div>
      <div className="flex flex-col space-y-2">
        <div className="flex items-center">
          <FormLabel className="form-label mr-2">Nivel de profundidad</FormLabel>
          {isConflict && (
            <Tooltip align="start" content="Ver detalle de profundidad">
              <button
                type="button"
                className="inline-flex items-center justify-center"
                onClick={() => handleOpenInfoPanel(depthLevelKey)}
                aria-label="Más información sobre nivel de profundidad"
              >
                <TooltipInformation />
              </button>
            </Tooltip>
          )}
        </div>

        <div className="grid grid-cols-[80px_1fr] items-center gap-4">
          <div className="text-sm text-left leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 font-normal mr-4">
            Familiar
          </div>
          <FormField
            control={form.control}
            name="level_a"
            render={({ field }) => (
              <FormItem className="flex flex-col h-[32px]">
                <FormControl>
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={String(field.value || '')}
                    disabled={isBasic || !selectedEnrichmentType || isViewMode}
                  >
                    <SelectTrigger
                      className={`justify-end w-[60px] h-[32px] border-gray-300 shadow-sm ${
                        isBasic ? 'bg-gray-100 cursor-not-allowed text-grey-3' : ''
                      }`}
                    >
                      <SelectValue placeholder="" />
                    </SelectTrigger>
                    <SelectContent className="bg-white !min-w-[60px]">
                      {[1, 2, 3].map((level) => (
                        <SelectItem key={level} value={String(level)}>
                          {level}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-[80px_1fr] items-center gap-4">
          <div className="text-sm text-left leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 font-normal mr-4">
            Societaria
          </div>
          <FormField
            control={form.control}
            name="level_b"
            render={({ field }) => (
              <FormItem className="flex flex-col h-[32px]">
                <FormControl>
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={String(field.value || '')}
                    disabled={isBasic || !selectedEnrichmentType || isViewMode}
                  >
                    <SelectTrigger
                      className={`justify-end w-[60px] h-[32px] border-gray-300 shadow-sm ${
                        isBasic ? 'bg-gray-100 cursor-not-allowed text-grey-3' : ''
                      }`}
                    >
                      <SelectValue placeholder="" />
                    </SelectTrigger>
                    <SelectContent className="bg-white !min-w-[60px]">
                      {[1, 2, 3].map((level) => (
                        <SelectItem key={level} value={String(level)}>
                          {level}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Panel lateral con información detallada del tipo de proceso */}
      <ProcessInfoPanel
        open={infoPanelKey !== null}
        onClose={handleCloseInfoPanel}
        processKey={infoPanelKey}
      />
    </>
  )
}
